import {
  type Dispatch,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import {
  isEmpty,
  isNotDeleted,
  isNotEmpty,
  isWorkflowActionId,
  pluralize,
  searchIncludes,
  splitArray,
  type WorkflowAction,
  type WorkflowActionId,
  WORKFLOW_STAGE_CHANGE_ACTION_ID,
} from '@terros/common'
import {
  CloseButton,
  Combobox,
  Input,
  type InputBaseProps,
  Pill,
  PillsInput,
  Tooltip,
  useCombobox,
} from '@mantine/core'
import { useTranslation } from '@front/model'
import { MaterialSymbolIcon, WorkflowIconDisplay } from '../terros'
import {
  MultiSelectFilterOption,
  type MultiSelectFilterBehavior,
  type MultiSelectFilterRowAction,
  useMultiSelectFilterState,
} from '../pickers'

type ActionMultiSelectProps = {
  value: WorkflowActionId[]
  setValue: Dispatch<SetStateAction<WorkflowActionId[]>>
  actions: WorkflowAction[]
  inputProps?: InputBaseProps
  allowArchived?: boolean
  showTransition?: boolean
  placeholder?: string
  compact?: boolean
  behavior: MultiSelectFilterBehavior
}

export const WorkflowActionMultiSelect = ({
  value,
  setValue,
  actions,
  inputProps,
  allowArchived,
  showTransition,
  placeholder,
  compact = false,
  behavior,
}: ActionMultiSelectProps): ReactElement => {
  const { t } = useTranslation()
  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const [search, setSearch] = useState('')

  const allowSelectActions = useMemo(
    () => (allowArchived ? actions : actions.filter(isNotDeleted)),
    [actions, allowArchived]
  )
  const allActionIds = useMemo(() => {
    const ids = allowSelectActions.map((action) => action.id)
    if (showTransition) ids.push(WORKFLOW_STAGE_CHANGE_ACTION_ID)
    return ids
  }, [allowSelectActions, showTransition])

  const { isAllSelected, selectedCount, isSelected, toggle, selectRow, selectAll, getRowAction, doesToggleSelectAll } =
    useMultiSelectFilterState({
      allValues: allActionIds,
      selectedValues: value,
      setSelectedValues: setValue,
      behavior,
    })

  const actionsMap = useMemo(
    () => new Map<WorkflowActionId, WorkflowAction>(actions.map((action) => [action.id, action])),
    [actions]
  )
  const filtered = useMemo(
    () =>
      actions.filter((action) =>
        searchIncludes([action.id, action.name, action.description, action.short, action.plural], search)
      ),
    [actions, search]
  )
  const isTransitionVisible = useMemo(() => {
    if (!showTransition) return false
    if (isEmpty(search)) return true
    return searchIncludes([t('Stage Transition'), 'Stage Transition', WORKFLOW_STAGE_CHANGE_ACTION_ID], search)
  }, [search, showTransition, t])

  const comboboxOptions = useMemo(() => {
    if (isEmpty(filtered) && !isTransitionVisible) return <Combobox.Empty>{t('No actions found')}</Combobox.Empty>
    return getFilteredActionSelectOptions({
      filtered,
      allowArchived,
      showTransition: isTransitionVisible,
      isItemSelected: isSelected,
      onItemToggle: toggle,
      getItemRowAction: getRowAction,
      doesItemToggleSelectAll: doesToggleSelectAll,
      stageTransitionLabel: t('Stage Transition'),
    })
  }, [allowArchived, doesToggleSelectAll, filtered, getRowAction, isSelected, isTransitionVisible, t, toggle])

  const handleOptionSelect = useCallback(
    (selectedValue: string): void => {
      if (!isWorkflowActionId(selectedValue)) return
      selectRow(selectedValue)
    },
    [selectRow]
  )

  const displayValue = useMemo(() => {
    if (isEmpty(value))
      return (
        <Input.Placeholder>
          {placeholder ?? (behavior.emptyBehavior === 'all' ? t('All Actions') : t('No action selected'))}
        </Input.Placeholder>
      )
    if (isAllSelected) return <Input.Placeholder>{t('All Actions')}</Input.Placeholder>
    if (compact) return `${selectedCount} ${pluralize(selectedCount, t('action'), t('actions'))}`
    return (
      <Pill.Group>
        {value.map((item) => (
          <Pill key={item} withRemoveButton onRemove={() => toggle(item)}>
            {item === WORKFLOW_STAGE_CHANGE_ACTION_ID
              ? t('Stage Transition')
              : (actionsMap.get(item)?.data.name ?? item)}
          </Pill>
        ))}
      </Pill.Group>
    )
  }, [actionsMap, behavior.emptyBehavior, compact, isAllSelected, placeholder, selectedCount, t, toggle, value])

  const rightSection = useMemo(() => {
    if (isEmpty(value) || isAllSelected || !compact) return <Combobox.Chevron />
    return (
      <CloseButton
        size='sm'
        aria-label={t('Check all actions')}
        onClick={(event) => {
          event.stopPropagation()
          selectAll()
        }}
      />
    )
  }, [compact, isAllSelected, selectAll, t, value])

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSelect} position='bottom-start' width={360}>
      <Combobox.Target>
        <PillsInput
          flex={compact ? undefined : 1}
          {...inputProps}
          pointer
          onClick={() => combobox.toggleDropdown()}
          rightSection={rightSection}
          rightSectionPointerEvents='all'
        >
          {displayValue}
        </PillsInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t('Search actions')}
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {comboboxOptions}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

type FilteredActionSelectOptionsProps = {
  filtered: WorkflowAction[]
  allowArchived?: boolean
  showTransition?: boolean
  isItemSelected: (actionId: WorkflowActionId) => boolean
  onItemToggle: (actionId: WorkflowActionId) => void
  getItemRowAction: (actionId: WorkflowActionId) => MultiSelectFilterRowAction
  doesItemToggleSelectAll: (actionId: WorkflowActionId) => boolean
  stageTransitionLabel: string
}

const getFilteredActionSelectOptions = ({
  filtered,
  allowArchived,
  showTransition,
  isItemSelected,
  onItemToggle,
  getItemRowAction,
  doesItemToggleSelectAll,
  stageTransitionLabel,
}: FilteredActionSelectOptionsProps): ReactNode => {
  const { yes: activeActions, no: archivedActions } = splitArray(filtered, isNotDeleted)
  const options = []
  if (isNotEmpty(activeActions)) {
    options.push(
      <Combobox.Group key='active' label='Actions'>
        {activeActions.map((action) => (
          <ActionSelectOption
            key={action.id}
            action={action}
            isSelected={isItemSelected(action.id)}
            onToggle={onItemToggle}
            rowAction={getItemRowAction(action.id)}
            doesToggleSelectAll={doesItemToggleSelectAll(action.id)}
          />
        ))}
      </Combobox.Group>
    )
  }
  if (isNotEmpty(archivedActions)) {
    options.push(
      <Combobox.Group key='archived' label='Archived'>
        {archivedActions.map((action) => (
          <ActionSelectOption
            key={action.id}
            action={action}
            isSelected={isItemSelected(action.id)}
            disabled={!allowArchived}
            onToggle={onItemToggle}
            rowAction={getItemRowAction(action.id)}
            doesToggleSelectAll={doesItemToggleSelectAll(action.id)}
          />
        ))}
      </Combobox.Group>
    )
  }
  if (showTransition) {
    options.push(
      <Combobox.Group key='transition' label='Other'>
        <MultiSelectFilterOption
          value={WORKFLOW_STAGE_CHANGE_ACTION_ID}
          label={stageTransitionLabel}
          isSelected={isItemSelected(WORKFLOW_STAGE_CHANGE_ACTION_ID)}
          doesToggleSelectAll={doesItemToggleSelectAll(WORKFLOW_STAGE_CHANGE_ACTION_ID)}
          leftSection={<MaterialSymbolIcon icon='sync_alt' size='lg' />}
          onToggle={() => onItemToggle(WORKFLOW_STAGE_CHANGE_ACTION_ID)}
          rowAction={getItemRowAction(WORKFLOW_STAGE_CHANGE_ACTION_ID)}
        />
      </Combobox.Group>
    )
  }
  return options
}

const ActionSelectOption = ({
  action,
  isSelected,
  disabled,
  tooltip,
  onToggle,
  rowAction,
  doesToggleSelectAll,
}: {
  action: WorkflowAction
  isSelected: boolean
  disabled?: boolean
  tooltip?: string
  onToggle: (actionId: WorkflowActionId) => void
  rowAction: MultiSelectFilterRowAction
  doesToggleSelectAll: boolean
}): ReactElement => (
  <OptionWrapper tooltip={tooltip}>
    <MultiSelectFilterOption
      value={action.id}
      disabled={disabled}
      label={action.name}
      isSelected={isSelected}
      doesToggleSelectAll={doesToggleSelectAll}
      leftSection={<WorkflowIconDisplay {...action.getDisplayProps()} />}
      onToggle={() => onToggle(action.id)}
      rowAction={rowAction}
    />
  </OptionWrapper>
)

const OptionWrapper = ({ children, tooltip }: PropsWithChildren<{ tooltip?: string }>): ReactNode => {
  if (isNotEmpty(tooltip)) return <Tooltip label={tooltip}>{children}</Tooltip>
  return children
}
